import 'index.css';
import '@lib/utils/polyfills';

import React from 'react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { RelayEnvironmentProvider } from 'react-relay/hooks';
import { applyRouterMiddleware, browserHistory, Router } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
// @ts-ignore
import { useScroll } from 'react-router-scroll';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { init as initiFullstory } from '@fullstory/browser';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import { Buffer } from 'buffer';
import configureStore from 'configureStore';
import { getTheme } from 'core/styles/getTheme';
import translationMessages from 'i18n/translationMessages';
import { createRoutes } from 'routes';
import * as serviceWorker from 'serviceWorker';
import smoothscroll from 'smoothscroll-polyfill';
import { getQueryConfig } from 'ui-model';

import { LanguageProvider } from '@lib/providers';
import axiosClientService from '@lib/services/axiosClientService';
import configService from '@lib/services/configService';
import storageService from '@lib/services/storageService';
import axiosClient from '@lib/utils/axiosClient';
import RelayEnvironment from '@lib/utils/RelayEnvironment';
import storageClient from '@lib/utils/storageClient';

import { makeSelectLocationState } from 'containers/App/selectors';

import ModulesProvider from './ModulesProvider';

// Uncomment the lines below if you wanna mock the network,
// this way it will use the handlers of "mocks/handlers" instead of BE api

// if (process.env.NODE_ENV === 'development') {
//   const { axiosService } = require('ui-model');
//   const { TEST_BASE_URL } = require('./tests/constants');
//   // set axiosService with the same baseUrl used on test env
//   // or you can change the BASE_URL value of "tests/constants" to use the API of your env
//   axiosService.defaults.baseURL = TEST_BASE_URL;
//   axiosClient.defaults.baseURL = TEST_BASE_URL;
//   const { worker } = require('./mocks/browser');
//   worker.start({ waitUntilReady: true });
// }

// TODO move to `@lib/utils/polyfills` once we're sure it doesn't cause problems for patient-app in prod
smoothscroll.polyfill();

(window as any).commitHash = configService.getCommitHash();
(window as any).Buffer = Buffer;

// Initialize additional GTM container (from URL parameter or .env)
const gtmIdAdditional = new URLSearchParams(window.location.search).get('gtmId') || configService.getGtmIdAdditional();
if (gtmIdAdditional) {
  TagManager.initialize({ gtmId: gtmIdAdditional });
}

// NOTE: Set up different shared-lib services
axiosClientService.set(axiosClient);
storageService.setStorage(storageClient);

// Initialize Datadog
if (process.env.REACT_APP_POD_NAME === 'production') {
  datadogRum.init({
    applicationId: '05dc40ee-65b9-4154-889c-d25524677c48',
    clientToken: 'pubb266980d896871978b24a34b65e1d1de',
    site: 'datadoghq.com',
    service: 'patient-web-app',
    env: process.env.REACT_APP_POD_NAME,
    version: configService.getCommitHash(),
    sessionSampleRate: 20,
    sessionReplaySampleRate: 20,
    startSessionReplayRecordingManually: true,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    allowedTracingUrls: [/https:\/\/.*\.lumahealth\.io/],
  });

  datadogLogs.init({
    clientToken: 'pubb266980d896871978b24a34b65e1d1de',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
  });
}

initiFullstory({
  orgId: process.env.REACT_APP_FULLSTORY_ID || '',
  namespace: `patient-app-fullstory`,
  devMode: process.env.REACT_APP_POD_NAME !== 'production',
});

// Create redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const store = configureStore(browserHistory);

// Sync history and store, as the react-router-redux reducer
// is under the non-default key ("routing"), selectLocationState
// must be provided for resolving how to retrieve the "route" in the state
const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: makeSelectLocationState(),
});

// Set up the router, wrapping all Routes in the App component
const routes = createRoutes(store);

const muiTheme = getTheme();

const queryClient = new QueryClient(getQueryConfig());

ReactDOM.render(
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <MuiThemeProvider theme={muiTheme}>
        <LanguageProvider messages={translationMessages}>
          <RelayEnvironmentProvider environment={RelayEnvironment}>
            <ModulesProvider>
              <Router
                history={history}
                routes={routes}
                render={
                  // Scroll to top when going to a new page, imitating default browser
                  // behaviour
                  applyRouterMiddleware(useScroll()) // eslint-disable-line
                }
              />
            </ModulesProvider>
          </RelayEnvironmentProvider>
        </LanguageProvider>
      </MuiThemeProvider>
    </Provider>
  </QueryClientProvider>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
